import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { siteSections } from "@/data/siteMap";
import { ArrowUpRight } from "lucide-react";

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Insights() {
  const meta = siteSections.insights;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = activeCategory ? { category: activeCategory } : {};
        const { data } = await api.get("/insights", { params });
        setPosts(data);
      } finally { setLoading(false); }
    })();
  }, [activeCategory]);

  const categoryLabel = useMemo(() => {
    if (!activeCategory) return null;
    return meta.items.find((i) => i.slug === activeCategory)?.label || activeCategory;
  }, [activeCategory, meta.items]);

  const setCategory = (slug) => {
    if (slug) setSearchParams({ category: slug });
    else setSearchParams({});
  };

  return (
    <div className="bg-[#0B0C10]" data-testid="insights-page">
      {/* Hero */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">{meta.landing.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            {meta.landing.title}
          </h1>
          <p className="mt-6 font-display text-xl text-[#FF3355] max-w-3xl">{meta.landing.tagline}</p>
          <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed">{meta.landing.intro}</p>
        </div>
      </section>

      {/* Filter */}
      <section className="border-b border-[#1F2833] py-8 overflow-x-auto">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setCategory("")}
            data-testid="insights-filter-all"
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] border ${
              !activeCategory ? "bg-[#B91C2C] text-[#0B0C10] border-[#B91C2C]" : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
            }`}
          >
            All
          </button>
          {meta.items.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCategory(c.slug)}
              data-testid={`insights-filter-${c.slug}`}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] border ${
                activeCategory === c.slug ? "bg-[#B91C2C] text-[#0B0C10] border-[#B91C2C]" : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">
            // {categoryLabel ? categoryLabel : "ALL POSTS"} · {posts.length}
          </div>
          {loading ? (
            <div className="font-mono text-sm text-[#C5C6C7]">&gt; loading<span className="te-caret">_</span></div>
          ) : posts.length === 0 ? (
            <div className="border border-[#1F2833] p-10 text-center text-[#C5C6C7]" data-testid="insights-empty">
              No posts in this category yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/insights/${p.slug}`}
                  data-testid={`insight-card-${p.slug}`}
                  className="bg-[#0B0C10] p-6 te-glow-hover border border-transparent hover:border-[#B91C2C] flex flex-col"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">
                      {meta.items.find((c) => c.slug === p.category)?.label || p.category}
                    </span>
                    <ArrowUpRight size={14} className="text-[#FF3355] opacity-60" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-[#F5F5F7]">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#C5C6C7] leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/60">
                    {p.author} · {formatDate(p.published_at)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
